import { LitElement, html, nothing, unsafeCSS, TemplateResult } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';
import { TimelineEvent, TimelineItem } from '../types';
import { styles as componentStyles } from '../view/timelineItemStyles.scss';
import { isElementOverflowed, OverflowDirection } from '../../../utils/htmlElement.utils';
import { GzTooltip } from '../../gzTooltip';
import { isEmpty } from '@nirvana/shared/basic-types/object';

/**
 * @summary This component represents a single item in the collapsible timeline component.
 * @property {boolean} isOpen - The expanded/collapsed state of the item.
 * @property {string} id - The unique identifier for the item. It is used to track the open/closed state of the item.
 * @property {string} label - The displayed label for the item.
 * @property {boolean} isEmphasized - Indicates whether the item is emphasized. If set to true, the timeline bullet will be highlighted with a border.
 * @property {string} details - Additional details about the item, which are displayed below the label.
 * @tag gz-collapsible-timeline-item
 * @event {CustomEvent} toggleItem - fires when a single timeline item is expanded or collapsed via user action. The event contains the id of the toggled element, and it's used within the 'gz-collapsible-timeline-group' to manage the group's behavior.
 */
@customElement('gz-collapsible-timeline-item')
export class GzCollapsibleTimelineItemWebComponent extends LitElement implements TimelineItem {
  static get styles() {
    return [unsafeCSS(`${componentStyles}`)];
  }

  /**
   * Sets the expanded/collapsed state of the component. This behavior is handled by the parent component (gz-collapsible-timeline-group)
   */
  @property({ type: Boolean })
  isOpen = false;

  /**
   * Unique identifier for the item. It is used to track the open/closed state of the item
   */
  @property({ type: String })
  id: string;

  /**
   * Label for the item. This field is mandatory
   */
  @property({ type: String })
  label: string;

  /**
   * Indicates whether the item is emphasized. If set to true, the timeline bullet will be highlighted with a border.
   */
  @property({ type: Boolean })
  isEmphasized = false;

  /**
   * Additional details about the item, which are displayed below the label. This field is optional
   */
  @property({ type: String })
  details?: string;

  /**
   * Number of grouped entries for the item. If set, a count badge will be displayed on the timeline bullet
   */
  @property({ type: Number, reflect: true, attribute: true })
  groupedEntriesCount?: number;

  /**
   * Renderer function for the suffix content of the item.
   */
  suffixRenderer?: () => TemplateResult | undefined;

  /**
   * Renderer function for the content of the item. When the item is expanded, this content will be displayed below the item summary
   */
  contentRenderer?: () => TemplateResult | undefined;

  @query('#summary')
  private summaryElement!: HTMLElement;

  constructor() {
    super();
    // Listen for custom events from child items
    this.addEventListener('mouseenter', e => this.onMouseEnter(e));
    this.addEventListener('mousemove', GzTooltip.align);
    this.addEventListener('mouseleave', GzTooltip.hide);
  }

  render() {
    return html`
      <gz-collapsible-panel
        hideToggle
        class="item-panel"
        ?isOpen=${this.isOpen}
        @panelHeaderClick=${this.onHeaderClick}
        part="collapsible-panel"
      >
        <div slot="panel-header">
          <div class="timeline-item">
            <div class="timeline-item--bullet">${this.groupedEntriesCountRenderer()}</div>
            <div class="timeline-item--summary">
              <div class="summary" id="summary">${this.summaryRenderer(this.label, this.details)}</div>
              ${this.suffixRenderer ? html`<div class="suffix">${this.suffixRenderer()}</div>` : nothing}
            </div>
          </div>
        </div>
        ${this.contentRenderer ? html`<div class="timeline-item--content">${this.contentRenderer()}</div>` : nothing}
      </gz-collapsible-panel>
    `;
  }

  private summaryRenderer(label: string, details?: string): TemplateResult {
    return html`
      <div class="timeline-item--label">${label}</div>
      ${!isEmpty(details) ? html`<div class="timeline-item--details">${details}</div>` : nothing}
    `;
  }

  private groupedEntriesCountRenderer(): TemplateResult {
    if (isEmpty(this.groupedEntriesCount)) {
      return html`<div class="circle"></div>`;
    } else {
      return html`<div class="circle has-count">
        <span class="count"> ${this.groupedEntriesCount} </span>
      </div>`;
    }
  }

  private onHeaderClick(): void {
    this.dispatchEvent(
      new CustomEvent<{ id: string }>(TimelineEvent.TOGGLE_ITEM, {
        detail: { id: this.id },
        bubbles: true,
        composed: true,
      })
    );
  }

  private onMouseEnter(event: MouseEvent): void {
    if (!this.summaryElement || !isElementOverflowed(this.summaryElement, OverflowDirection.HORIZONTAL)) {
      return;
    }
    const tooltipText = !isEmpty(this.details) ? `${this.label} (${this.details})` : this.label;
    GzTooltip.show(tooltipText, event);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-collapsible-timeline-item': GzCollapsibleTimelineItemWebComponent;
  }
}
